/**
 * auth.ts — Auth utilities for token management.
 *
 * Email magic-link sessions use an httpOnly cookie (relay_session) set by
 * the backend on verify. The JWT never touches JavaScript — only a non-httpOnly
 * flag cookie (relay_session_exists) is readable here to detect an active
 * session without exposing the token to XSS.
 */

/**
 * Returns true when the browser holds an active email magic-link session.
 * Reads the non-httpOnly relay_session_exists cookie set by the backend.
 */
export function hasCookieSession(): boolean {
  try {
    return document.cookie
      .split(";")
      .some((c) => c.trim().startsWith("relay_session_exists="));
  } catch {
    return false;
  }
}

/**
 * Decode JWT claims without verification (client-side only).
 * WARNING: Does NOT verify the token signature. Use only for non-sensitive
 * metadata on tokens the caller already holds (e.g. MSAL access tokens).
 */
export function decodeJwt(token: string): Record<string, unknown> | null {
  try {
    const [, payload] = token.split(".");
    if (!payload) return null;
    return JSON.parse(atob(payload));
  } catch {
    return null;
  }
}

/**
 * Check if a JWT token string is expired.
 * Returns false (not expired) if the token has no exp claim.
 */
export function isTokenExpired(token: string): boolean {
  const claims = decodeJwt(token);
  if (!claims || typeof claims.exp !== "number") return false;
  return Date.now() >= claims.exp * 1000;
}

/**
 * Set up auto-logout when a token (held in memory, not localStorage) expires.
 * Returns a cleanup function to cancel the timer.
 */
export function setupTokenExpiryHandler(
  token: string,
  onExpired: () => void,
): () => void {
  const claims = decodeJwt(token);
  if (!claims || typeof claims.exp !== "number") return () => {};
  const expiresIn = Math.max(0, claims.exp * 1000 - Date.now());
  if (expiresIn === 0) return () => {};
  // Trigger 5 minutes before expiry (or immediately if < 5 min left)
  const delay = Math.max(0, expiresIn - 5 * 60 * 1000);
  const id = setTimeout(onExpired, delay);
  return () => clearTimeout(id);
}
